import React, { useState, useRef, useEffect } from 'react'
import './CustomSelect.scss'

interface CustomSelectProps {
  value: string | number
  onChange: (event: any) => void
  options: Array<{ value: string | number; label: string }>
  placeholder?: string
  showRequiredStarInPlaceholder?: boolean
  className?: string
  maxDropdownHeight?: string
  disabled?: boolean
}

export const CustomSelect: React.FC<CustomSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  showRequiredStarInPlaceholder = false,
  className = '',
  maxDropdownHeight = '200px',
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const selectRef = useRef<HTMLDivElement>(null)

  // Get selected option label
  const selectedOption = options.find(opt => opt.value === value)
  const displayText = selectedOption ? selectedOption.label : placeholder
  const isPlaceholder = !selectedOption || value === '' || value === null || value === undefined || displayText.toLowerCase().startsWith('select')

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleSelect = (optionValue: string | number) => {
    // Create a synthetic event similar to native select
    const syntheticEvent = {
      target: {
        value: optionValue
      }
    }
    onChange(syntheticEvent)
    setIsOpen(false)
  }

  return (
    <div className={`custom-select ${className} ${disabled ? 'disabled' : ''}`} ref={selectRef}>
      <div 
        className={`custom-select-trigger ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span className={isPlaceholder ? 'placeholder' : ''}>
          {displayText}
          {isPlaceholder && showRequiredStarInPlaceholder && <span className="required-star"> *</span>}
        </span>
        <svg 
          className="custom-select-arrow" 
          width="24" 
          height="24" 
          viewBox="0 0 24 24" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
          style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
        >
          <path 
            d="M19.9201 8.9502L13.4001 15.4702C12.6301 16.2402 11.3701 16.2402 10.6001 15.4702L4.08008 8.9502" 
            stroke="currentColor" 
            strokeWidth="1.5" 
            strokeMiterlimit="10" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
        </svg>
      </div>
      
      {isOpen && (
        <div 
          className="custom-select-dropdown" 
          style={{ maxHeight: maxDropdownHeight }}
        >
          {options.map((option) => (
            <div
              key={option.value}
              className={`custom-select-option ${option.value === value ? 'selected' : ''}`}
              onClick={() => handleSelect(option.value)}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

